import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { 
    FaUser, FaEnvelope, FaPhone, FaLock, FaCamera, FaSave, 
    FaInfoCircle, FaMapMarkerAlt, FaClock, FaWhatsapp, FaFacebook, 
    FaInstagram, FaTwitter, FaLinkedin, FaGlobe, FaCheckCircle
} from 'react-icons/fa';
import { fetchProfile, updateProfile, fetchAboutData } from '../../redux/actions/profileAction';
import { resetProfileState } from '../../redux/slices/profileSlice';
import './Profile.css';

const Profile = () => {
    const { t, i18n } = useTranslation('global');
    const dispatch = useDispatch();
    const currentLang = i18n.language;

    const { 
        data: userData, 
        isLoading, 
        isUpdating, 
        success, 
        updateError,
        aboutData,
        aboutLoading
    } = useSelector((state) => state.profile);

    const [activeTab, setActiveTab] = useState('personal');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        password_confirmation: '',
    });

    useEffect(() => {
        const token = localStorage.getItem('token');
        const isSocial = userData?.isSocial || token === 'social_login_placeholder';
        
        if (token && !isSocial) {
            dispatch(fetchProfile(currentLang));
        }
        dispatch(fetchAboutData(currentLang));
    }, [dispatch, currentLang, userData?.isSocial]);

    useEffect(() => {
        if (userData) {
            setFormData({
                name: userData.name || '',
                email: userData.email || '',
                phone: userData.phone || '',
                password: '',
                password_confirmation: '',
            });
        }
    }, [userData]);

    useEffect(() => {
        if (success) {
            toast.success(t('profile.update_success'));
            dispatch(resetProfileState());
        }
        if (updateError) {
            toast(updateError);
            dispatch(resetProfileState());
        }
    }, [success, updateError, dispatch, t]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Basic validation
        if (formData.password) {
            if (formData.password.length < 8) {
                toast.info(i18n.language === 'ar' ? "كلمة المرور يجب أن تكون 8 أحرف على الأقل" : "Password must be at least 8 characters");
                return;
            }
            if (formData.password !== formData.password_confirmation) {
                toast.info(t('profile.password_mismatch'));
                return;
            }
        }

        const submitData = new FormData();
        submitData.append('name', formData.name);
        submitData.append('phone', formData.phone);
        submitData.append('email', formData.email);
        if (formData.password) {
            submitData.append('password', formData.password);
            submitData.append('password_confirmation', formData.password_confirmation);
        }

        dispatch(updateProfile({ formData: submitData, lang: currentLang }));
    };

    if (isLoading && !userData) {
        return (
            <div className="profile-loading">
                <div className="loader"></div>
            </div>
        );
    }

    return (
        <div className="profile-page-container">
            <div className="profile-bg-overlay"></div>
            <div className="profile-blobs">
                <div className="profile-blob"></div>
                <div className="profile-blob"></div>
            </div>

            <div className="profile-content">
                <header className="profile-header">
                    <div className="profile-header-main">
                        <div className="profile-avatar-wrapper">
                            <div className="profile-avatar">
                                {userData?.photo || userData?.image ? (
                                    <img src={userData.photo || userData.image} alt={userData.name} />
                                ) : (
                                    userData?.name ? userData.name.charAt(0).toUpperCase() : <FaUser />
                                )}
                            </div>
                        </div>
                        <div className="profile-title-info">
                            <h1>{userData?.name || t('profile.user')}</h1>
                            <p>{userData?.email || 'user@example.com'}</p>
                            <div className="profile-meta">
                                <span className="profile-status-badge">
                                    <FaCheckCircle /> {t('profile.verified_account')}
                                </span>
                                {userData?.created_at && (
                                    <span className="profile-joined-date">
                                        <FaClock /> {t('profile.joined')} {userData.created_at}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <nav className="profile-tabs">
                        <button 
                            className={`profile-tab-btn ${activeTab === 'personal' ? 'active' : ''}`}
                            onClick={() => setActiveTab('personal')}
                        >
                            <FaUser /> {t('profile.personal_info')}
                        </button>
                        <button 
                            className={`profile-tab-btn ${activeTab === 'about' ? 'active' : ''}`}
                            onClick={() => setActiveTab('about')}
                        >
                            <FaInfoCircle /> {t('profile.about_oneplus')}
                        </button>
                    </nav>
                </header>

                <main className="profile-main-card">
                    {activeTab === 'personal' ? (
                        <div className="profile-section fade-in">
                            <div className="section-header">
                                <h2>{t('profile.edit_profile')}</h2>
                                <p>{t('profile.edit_description')}</p>
                            </div>
                            <form onSubmit={handleSubmit} className="profile-form">
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label><FaUser /> {t('profile.full_name')}</label>
                                        <input 
                                            type="text" 
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder={t('profile.enter_name')}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label><FaEnvelope /> {t('profile.email')}</label>
                                        <input 
                                            type="email" 
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder={t('profile.enter_email')}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label><FaPhone /> {t('profile.phone')}</label>
                                        <input 
                                            type="text" 
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            placeholder={t('profile.enter_phone')}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label><FaLock /> {t('profile.new_password')}</label>
                                        <input 
                                            type="password" 
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            placeholder={t('profile.enter_new_password')}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label><FaLock /> {t('profile.confirm_password')}</label>
                                        <input 
                                            type="password" 
                                            name="password_confirmation"
                                            value={formData.password_confirmation}
                                            onChange={handleInputChange}
                                            placeholder={t('profile.confirm_new_password')}
                                        />
                                    </div>
                                </div>
                                <div className="form-actions">
                                    <button type="submit" className="save-btn" disabled={isUpdating}>
                                        {isUpdating ? (
                                            <span className="profile-spinner"></span>
                                        ) : (
                                            <><FaSave /> {t('profile.save_changes')}</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="profile-section fade-in">
                            {aboutLoading ? (
                                <div className="section-loading">
                                    <div className="loader small"></div>
                                </div>
                            ) : (
                                <div className="about-details">
                                    <div className="about-brand-info">
                                        <h2>{aboutData?.about_title || 'About OnePlus'}</h2>
                                        <p className="about-desc">{aboutData?.about_description}</p>
                                    </div>

                                    <div className="info-cards-grid">
                                        <div className="info-card">
                                            <div className="info-icon"><FaMapMarkerAlt /></div>
                                            <div className="info-text">
                                                <h3>{t('profile.address')}</h3>
                                                <p>{aboutData?.stay_in_touch?.address}</p>
                                            </div>
                                        </div>
                                        <div className="info-card">
                                            <div className="info-icon"><FaClock /></div>
                                            <div className="info-text">
                                                <h3>{t('profile.work_hours')}</h3>
                                                <p>{aboutData?.stay_in_touch?.work_hours}</p>
                                            </div>
                                        </div>
                                        <div className="info-card">
                                            <div className="info-icon"><FaPhone /></div>
                                            <div className="info-text">
                                                <h3>{t('profile.contact_phones')}</h3>
                                                <p>{aboutData?.stay_in_touch?.phones}</p>
                                            </div>
                                        </div>
                                        <div className="info-card">
                                            <div className="info-icon"><FaEnvelope /></div>
                                            <div className="info-text">
                                                <h3>{t('profile.email')}</h3>
                                                <p>{aboutData?.stay_in_touch?.email}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="social-links-section">
                                        <h3>{t('profile.social_links')}</h3>
                                        <div className="social-buttons">
                                            {aboutData?.social_links?.facebook && (
                                                <a href={aboutData.social_links.facebook} target="_blank" rel="noreferrer" className="social-btn fb">
                                                    <FaFacebook />
                                                </a>
                                            )}
                                            {aboutData?.social_links?.instagram && (
                                                <a href={aboutData.social_links.instagram} target="_blank" rel="noreferrer" className="social-btn ig">
                                                    <FaInstagram />
                                                </a>
                                            )}
                                            {aboutData?.social_links?.twitter && (
                                                <a href={aboutData.social_links.twitter} target="_blank" rel="noreferrer" className="social-btn tw">
                                                    <FaTwitter />
                                                </a>
                                            )}
                                            {aboutData?.social_links?.linkedin && (
                                                <a href={aboutData.social_links.linkedin} target="_blank" rel="noreferrer" className="social-btn li">
                                                    <FaLinkedin />
                                                </a>
                                            )}
                                            {aboutData?.stay_in_touch?.whatsapp_link && (
                                                <a href={aboutData.stay_in_touch.whatsapp_link} target="_blank" rel="noreferrer" className="social-btn wa">
                                                    <FaWhatsapp />
                                                </a>
                                            )}
                                            {aboutData?.stay_in_touch?.web_link && (
                                                <a href={aboutData.stay_in_touch.web_link} target="_blank" rel="noreferrer" className="social-btn web">
                                                    <FaGlobe />
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    {aboutData?.stay_in_touch?.map_link && (
                                        <div className="map-section">
                                            <h3>{t('profile.our_location')}</h3>
                                            <div className="map-container">
                                                <iframe 
                                                    src={aboutData.stay_in_touch.map_link} 
                                                    width="100%" 
                                                    height="250" 
                                                    style={{ border: 0, borderRadius: '15px' }} 
                                                    allowFullScreen="" 
                                                    loading="lazy"
                                                    title="OnePlus Location"
                                                ></iframe>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default Profile;
